// LR1-5 — HER MODERATION SEAT. Shipping WITH the room, not after.
//
// The queue (shadow-held, reported, everything), one-tap approve / hide / delete / warn, strikes
// wired to the escalation ladder, pins, and the ledger. Her presence as Lala with the owner badge is
// not built here — she posts in the room like everyone else and `publicIdentity` attaches the badge
// off the owner-email set, because a separate "post as owner" path would be a posting path that
// skips the gates.
//
// THE CONFIRM SENTENCE IS THE SERVER'S (the D2 law): this component asks for the sentence by
// composing the SAME call it is about to make, from the SAME shared module the server composes with,
// and the server returns its own sentence in the response. A confirm written on the client can
// describe something other than what the server then does.
import { useCallback, useEffect, useState } from "react";
import { loadOwnerData, ownerLoadNote, postOwnerAction, ownerActionNote } from "../lib/owner-load";
import { modActionSentence, INBOX_TAB_KEY, QUEUE_TABS } from "../livingRoom.js";
import { inboxDraftSentence } from "../inboxAnswer.js";
import { MemberDoor } from "./MemberDoor";
// 13-F2 — ONE ROW OF THE QUEUE, in its own file because this component holds seven hooks and nothing
// could CALL it. The row is where she decides, and it is now the thing a gate can drive.
import { ModerationRow, stamp } from "./ModerationRow";
import type { ModItem, ModAction } from "./ModerationRow";
// ITEM 12 — her DMs lane. Its own file, props in and tree out, so a gate CALLS it.
import { OwnerInboxLane } from "./OwnerInboxLane";
import type { InboxRow, InboxAction } from "./OwnerInboxLane";
// I1-I2 — the live lane: held and reported live comments, reported lives.
import { OwnerLiveLane } from "./OwnerLiveLane";
import type { LiveQueueComment, LiveQueueSession, LiveDecision } from "./OwnerLiveLane";

type Guideline = { code: string; rule: string; body: string };
// 13-F2 — the row's own type moved WITH the row (the standing rule in this repo). The queue's other
// readers below still speak in it, so it is imported back under the name they already used.
type Item = ModItem;
type Payload = {
  tab: string;
  tabs: Array<{ key: string; label: string; note: string }>;
  actions: ModAction[];
  posts: Item[];
  comments: Item[];
  avatars?: Item[];
  live?: { comments: LiveQueueComment[]; sessions: LiveQueueSession[] };
  stats: { posts: number; comments: number; held: number; reports: number; threads: number };
  log: Array<{ actor: string; action: string; itemKind: string; sentence: string; at: string }>;
  // LR1-15 — what she keeps overturning, and the phrases she has cleared.
  overturnPressure: Array<{ code: string; count: number; rule: string; flagged: boolean }>;
  allowances: Array<{ id: string; reason: string; phrase: string; note: string | null; at: string }>;
  guidelines: Guideline[];
  // ITEM 12 — present only on her DMs tab. Absent everywhere else, which is the honest shape: a lane
  // that is not being asked about returns nothing rather than an empty object pretending to be one.
  inbox?: { waiting: InboxRow[]; recent: InboxRow[]; actions: InboxAction[]; counts: { waiting: number; answered: number; drafted: number } };
};


// ITEM A — **THE LANE THIS SEAT OPENS ON IS A PARAMETER, BECAUSE THE ROSTER DECLARES TWO VIEWS.**
// `moderation` is her merged queue (everything held or flagged, which is what she came to act on) and
// `living` is the room seat whole (Everything, the photos, her DMs, the ledger). They were rendered
// by two BYTE-IDENTICAL lines in App.tsx for about an hour, which meant the menu offered two doors
// onto one surface — and R68's shared-literal ceiling is what caught it, in the round that wrote it.
//
// >>> **AND THAT REPAIR COULD NEVER TAKE EFFECT, WHICH ONLY A RENDERED WALK COULD SEE (ITEM 17's
// clause 2, 2026-08-11).** Both doors render THIS component at the SAME position in App.tsx's one
// twenty-arm ternary, so React reconciles them as ONE instance and does not remount — and
// `useState(lane)` reads its argument on the FIRST RENDER ONLY. So the lane prop was read once, when
// whichever door she opened first mounted the seat, and every later door handed a value nothing
// consumed: **tapping "The room seat" from "Needs you" drew the merged queue, on the merged queue's
// lane, with the sub-nav agreeing.** Two doors onto one surface, put back by the reconciler, with the
// parameter sitting right there in the source reading perfectly (`item-a-dashboard-walk.mjs` measured
// it as byte-identical markup at both viewports).
//
// **THE RESET LIVES HERE RATHER THAN AT THE TWO CALL SITES**, where a `key` would also work: a third
// door joins by EXISTING, and a call site that forgets is the shape this house has paid for over and
// over (R28/R29). It is React's own adjust-state-during-render pattern — state, not a ref, so it is
// correct under StrictMode — and it fires ONLY when the prop really moves, so her own tab taps below
// are untouched.
export function OwnerLivingRoom({ onDenied, onOpenMember, lane = QUEUE_TABS[0].key }: { onDenied: () => void; onOpenMember?: (id: string) => void; lane?: string }) {
  const [data, setData] = useState<Payload | null>(null);
  const [loadNote, setLoadNote] = useState("");
  const [tab, setTab] = useState(lane);
  const [laneSeen, setLaneSeen] = useState(lane);
  if (laneSeen !== lane) { setLaneSeen(lane); setTab(lane); }
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState<{ action: string; itemKind: string; id: string; authorName: string; sentence: string } | null>(null);
  // R66: TWO FACTS, TWO STATES. `ledgerNote` is what SHE types and what is recorded in the
  // append-only log; `actionNote` is what the SERVER said it did.
  const [ledgerNote, setLedgerNote] = useState("");
  const [actionNote, setActionNote] = useState<{ text: string; ok: boolean }>({ text: "", ok: true });
  // ITEM 12 — the rewrite box's text lives HERE so `OwnerInboxLane` stays hookless and callable.
  const [draftText, setDraftText] = useState<Record<string, string>>({});
  const [inboxConfirm, setInboxConfirm] = useState<{ row: InboxRow; action: string; text: string; sentence: string } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    loadOwnerData<Payload>(`/api/owner/living-room/queue?${new URLSearchParams({ tab })}`)
      .then((answer) => {
        if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
        if (answer.ok) setData(answer.data);
        setLoadNote(ownerLoadNote(answer));
      })
      .finally(() => setLoading(false));
  }, [tab, onDenied]);
  useEffect(() => { load(); }, [load]);

  // R66 — **`note` WAS ONE STATE DOING TWO JOBS AND THE COLLISION WROTE INTO AN APPEND-ONLY LOG.**
  // It is the textarea she types her ledger note into (POSTed as `note` and recorded in `lr_mod_log`,
  // whose migration-054 `DO INSTEAD NOTHING` rules mean there is a writer and a reader and NO
  // EDITOR) — and the line below ALSO assigned the SERVER'S response sentence to it. So after a warn,
  // the strike sentence sat in the ledger box, and **her NEXT moderation act posted words she never
  // typed into a log nothing on this site can change.** Two facts, two states.
  //
  // The rest is R65's finding one verb over, and worse: the act read its payload only through
  // `if (d?.ok)`, and the confirm closed REGARDLESS. A refused hide left the row exactly where it
  // was and said nothing — which does not read as a failure, it reads as a STUBBORN ROW, so she taps
  // hide again. And the fetch was not wrapped at all, so a thrown request rejected this handler
  // before `setConfirming(null)`: the confirm dialog stayed open forever with nothing said.
  const run = async () => {
    if (!confirming) return;
    const answer = await postOwnerAction<{ sentence?: string; strike?: { label?: string } | null }>(
      "/api/owner/living-room/action",
      {
        action: confirming.action, itemKind: confirming.itemKind, id: confirming.id,
        authorName: confirming.authorName, note: ledgerNote,
      },
    );
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    setActionNote({ text: ownerActionNote(answer), ok: answer.ok });
    // **HER TYPED LEDGER NOTE SURVIVES A REFUSAL**, and the confirm only closes on an act that
    // landed — retyping a reason she just wrote is the one thing a failed act must never cost her
    // (R61's bookmark ruling). Held by POSITION so no later spelling puts the reset back on top.
    if (!answer.ok) return;
    setConfirming(null);
    setLedgerNote("");
    load();
  };

  // ITEM 12 — **DOUBLE-POSTED, THE D2 LAW.** The first post changes nothing and hands back the
  // sentence the SERVER composed; the second carries `confirm: true` and the same payload. A confirm
  // written on the client can describe something other than what the server then does, and this one
  // sends words under her name into somebody's conversation.
  // R66 — **AND THIS IS THE SHARPEST OF THE FOUR, BECAUSE OF WHAT IT DOES.** The second post SENDS
  // WORDS UNDER HER NAME into a member's conversation, and it was `.catch(() => {})` followed by
  // closing the confirm and re-reading: **a refused send looked exactly like a sent one**, so she
  // believed a member had been answered and moved on. ITEM 12's own note on this handler says the
  // reason the confirm is double-posted is that a confirm written on the client can describe
  // something other than what the server does — and then it did not read what the server did.
  const runInbox = async () => {
    if (!inboxConfirm) return;
    const { row, action, text } = inboxConfirm;
    const answer = await postOwnerAction<{ sentence?: string }>("/api/owner/living-room/inbox-draft",
      { id: row.id, action, text, confirm: true });
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    setActionNote({ text: ownerActionNote(answer), ok: answer.ok });
    // The confirm stays up on a refusal, holding her rewritten text, so Send is one tap away rather
    // than a whole draft away (R61's ruling: a failed act must not cost her what she typed).
    if (!answer.ok) return;
    setInboxConfirm(null);
    load();
  };

  const askInbox = async (row: InboxRow, action: string, text: string) => {
    const r = await fetch("/api/owner/living-room/inbox-draft", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ id: row.id, action, text }),
    }).catch(() => null);
    const d = await r?.json().catch(() => null);
    setInboxConfirm({
      row, action, text,
      // The server's sentence when it answered; the shared composer's when it did not, so the box
      // still says what is about to happen rather than opening blank.
      sentence: d?.sentence || inboxDraftSentence({ action, memberName: row.memberName ? `@${row.memberName}` : "them" }),
    });
  };

  // R100 — **THE TWO ACTS R66 LEFT BEHIND, AND THIS IS THE FIRST.** It was
  // `await fetch(...).catch(() => {}); load();` — not the payload, not the status — so a refused pin
  // reloaded the queue and left the row exactly where it was. R66's finding word for word: **that
  // does not read as a failure, it reads as a STUBBORN ROW**, and she taps it again. The server has
  // always composed its own sentence for this one ("Pinned to the top of the room." / "Unpinned.")
  // and it had never once reached her.
  const pin = async (id: string, pinned: boolean) => {
    const answer = await postOwnerAction<{ sentence?: string }>("/api/owner/living-room/pin", { id, pinned });
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    setActionNote({ text: ownerActionNote(answer), ok: answer.ok });
    // R66's ruling: AN ACT THAT LANDED IS NOT SILENT, and one that did not must not leave the queue
    // looking as though it had. Held by POSITION so no later spelling reloads over a refusal.
    if (!answer.ok) return;
    load();
  };

  const items: Array<Item & { kind: "post" | "comment" | "avatar" }> = [
    // ITEM 4(c) — THE PHOTOS COME FIRST WHERE THERE ARE ANY. Her Photos tab returns only these; the
    // Held tab returns the ones she has already hidden, above the held words, because a face is the
    // fastest thing on this screen to judge and the slowest to explain after the fact.
    ...(data?.avatars || []).map((a) => ({ ...a, kind: "avatar" as const })),
    ...(data?.posts || []).map((p) => ({ ...p, kind: "post" as const })),
    ...(data?.comments || []).map((c) => ({ ...c, kind: "comment" as const })),
  ];

  // I1-I2 — her call on the live lane. One tap; the server's sentence says what it did, or why not.
  const decideLive = async (d: LiveDecision) => {
    const answer = await postOwnerAction<{ sentence?: string }>("/api/live/owner/decide", d);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    setActionNote({ text: ownerActionNote(answer), ok: answer.ok });
    if (!answer.ok) return;
    load();
  };
  const liveCount = (data?.live?.comments.length || 0) + (data?.live?.sessions.length || 0);

  return (
    <div className="owner-lr">
      {data ? (
        <div className="owner-stats owner-lr__stats">
          <div className="owner-stat"><strong>{data.stats.posts}</strong><span>Posts</span></div>
          <div className="owner-stat"><strong>{data.stats.comments}</strong><span>Comments</span></div>
          <div className="owner-stat"><strong>{data.stats.held}</strong><span>Held, author-only</span></div>
          <div className="owner-stat"><strong>{data.stats.reports}</strong><span>Open reports</span></div>
          <div className="owner-stat"><strong>{data.stats.threads}</strong><span>Conversations</span></div>
        </div>
      ) : null}

      <div className="owner-range owner-lr__tabs">
        {(data?.tabs || []).map((t) => (
          <button key={t.key} type="button" title={t.note}
            className={tab === t.key ? "owner-range__btn is-active" : "owner-range__btn"}
            onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>
      <p className="owner-page__note">{data?.tabs.find((t) => t.key === tab)?.note || ""}</p>

      {loading ? <p className="owner-page__note">Loading...</p> : null}
      {/* R65: a queue that did not ARRIVE is not a queue with nothing in it. "Nothing in here." over
          a failed read tells her the room is clean, which is the one wrong thing to tell a moderator
          (R60/R64: the failure state was not a missing sentence but a wrong one). */}
      {!loading && loadNote ? <p className="owner-page__note owner-fresh--failed" role="status">{loadNote}</p> : null}
      {/* R66: what the last act DID, or why it did not go. The server composes both (the D2 law). */}
      {actionNote.text ? <p className={actionNote.ok ? "owner-page__note owner-act-note" : "owner-page__note owner-fresh--failed"} role="status">{actionNote.text}</p> : null}
      {!loading && !loadNote && !items.length && !liveCount ? <p className="owner-page__note">Nothing in here.</p> : null}

      {tab === INBOX_TAB_KEY ? (
        <OwnerInboxLane
          waiting={data?.inbox?.waiting || []}
          recent={data?.inbox?.recent || []}
          actions={data?.inbox?.actions || []}
          counts={data?.inbox?.counts || { waiting: 0, answered: 0, drafted: 0 }}
          draftText={draftText}
          onDraftText={(id, text) => setDraftText((prev) => ({ ...prev, [id]: text }))}
          onAct={askInbox}
          onOpenMember={onOpenMember}
        />
      ) : null}

      <OwnerLiveLane
        comments={data?.live?.comments || []}
        sessions={data?.live?.sessions || []}
        onDecide={decideLive}
        onOpenMember={onOpenMember}
      />

      <div className="owner-lr__queue">
        {items.map((item) => (
          <ModerationRow
            key={`${item.kind}-${item.id}`}
            item={item}
            actions={data?.actions || []}
            onOpenMember={onOpenMember}
            onAct={(action) => setConfirming({
              action, itemKind: item.kind, id: item.id, authorName: item.author.name,
              // Composed from the SHARED module for the preview; the server composes its own
              // for the ledger, and `scripts/lr1-living-room.mjs` holds the two to each other.
              sentence: modActionSentence({ action, itemKind: item.kind, authorName: item.author.name }),
            })}
            onPin={(pinned) => pin(item.id, pinned)}
          />
        ))}
      </div>

      {confirming ? (
        <div className="modal-backdrop lr-modal-backdrop" role="dialog" aria-modal="true" aria-label="Confirm">
          <div className="lr-modal lr-modal--confirm">
            <h2>{confirming.sentence}</h2>
            {confirming.action === "warn" ? (
              <textarea rows={2} value={ledgerNote} placeholder="A note for the ledger (optional)" onChange={(e) => setLedgerNote(e.target.value)} />
            ) : null}
            <div className="owner-lr__confirm">
              {/* Never mind is HER choosing to abandon it, so clearing what she typed is right here
                  and wrong on a refusal — which is the whole distinction R66 draws. */}
              <button type="button" className="button button--ghost" onClick={() => { setConfirming(null); setLedgerNote(""); setActionNote({ text: "", ok: true }); }}>Never mind</button>
              <button type="button" className="button" onClick={run}>Do it</button>
            </div>
          </div>
        </div>
      ) : null}

      {inboxConfirm ? (
        <div className="modal-backdrop lr-modal-backdrop" role="dialog" aria-modal="true" aria-label="Confirm">
          <div className="lr-modal lr-modal--confirm">
            <h2>{inboxConfirm.sentence}</h2>
            {inboxConfirm.action === "send" ? <p className="owner-inbox__preview">{inboxConfirm.text}</p> : null}
            <div className="owner-lr__confirm">
              <button type="button" className="button button--ghost" onClick={() => setInboxConfirm(null)}>Never mind</button>
              <button type="button" className="button" onClick={runInbox}>Do it</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* LR1-15 — HER RESTORES TEACH. Arithmetic over her own overturns, so a class she keeps
          reversing shows itself here instead of waiting for her to notice the pattern. A flagged
          class is FLAGGED, never auto-relaxed: relaxing a gate because it fired a lot is how a gate
          gets talked out of the thing it was built for. The decision stays hers, and the way she
          makes it is the allowance box below. */}
      {data?.overturnPressure?.length ? (
        <>
          <h3 className="owner-lr__logtitle">What you keep overturning</h3>
          <ul className="owner-lr__pressure">
            {data.overturnPressure.map((o) => (
              <li key={o.code} className={o.flagged ? "is-flagged" : ""}>
                <strong>{o.count}</strong>
                <span>{o.rule}</span>
                {o.flagged ? <em>this class is probably wrong</em> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <AllowanceBox
        allowances={data?.allowances || []}
        guidelines={data?.guidelines || []}
        onChanged={load}
      />

      <h3 className="owner-lr__logtitle">What has been done</h3>
      <ul className="owner-lr__log">
        {(data?.log || []).map((l, i) => (
          <li key={i}><span>{stamp(l.at)}</span><strong>{l.actor}</strong>{l.sentence}</li>
        ))}
        {!data?.log.length ? <li className="owner-page__note">Nothing yet.</li> : null}
      </ul>
    </div>
  );
}

// LR1-15 — HER ALLOWANCES. The voice-ban mechanism in reverse: `server/lala-voice-bans.json` lets
// her ADD what a gate must catch, and this lets her declare what a class must STOP catching. Same
// owner-extensibility, opposite direction, and it is scoped PER CLASS on purpose — clearing "fake"
// for the family rule must not also clear it for harassment.
function AllowanceBox({
  allowances, guidelines, onChanged,
}: {
  allowances: Payload["allowances"];
  guidelines: Guideline[];
  onChanged: () => void;
}) {
  const [reason, setReason] = useState(guidelines[0]?.code || "");
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  // R100 — **THE SECOND ACT R66 LEFT BEHIND, AND IT IS THE WORSE OF THE TWO BECAUSE IT DESTROYS WHAT
  // SHE TYPED.** It was `await fetch(...).catch(() => {})` and then `setPhrase("")`, so a refused
  // write cleared her phrase, reloaded the list, and left it not there — **R61's ruling exactly:
  // retyping something she just wrote is the one thing a failed save must never cost her.** The
  // server has always answered 400 "Name the rule and the phrase." on this route, and that sentence
  // reached nobody. Held by POSITION so no later spelling puts the reset back above the guard.
  const send = async (body: Record<string, unknown>) => {
    setBusy(true);
    // The route answers with the whole list and no sentence of its own, so the answer is typed the
    // way `ownerActionNote` reads every other act on this seat rather than as a second shape.
    const answer = await postOwnerAction<{ sentence?: string }>("/api/owner/living-room/allowance", body);
    setBusy(false);
    setNote(ownerActionNote(answer));
    if (!answer.ok) return;
    setPhrase("");
    onChanged();
  };

  return (
    <div className="owner-lr__allow">
      <h3 className="owner-lr__logtitle">Phrases you have cleared</h3>
      <p className="owner-page__note">
        A phrase here stops the rule you pair it with from catching anything that contains it. It does
        not switch the rule off.
      </p>
      <div className="owner-lr__allowform">
        <select value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Which rule">
          {guidelines.map((g) => <option key={g.code} value={g.code}>{g.rule}</option>)}
        </select>
        <input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder="The phrase to allow" aria-label="Phrase" />
        <button type="button" className="button button--ghost" disabled={busy || !phrase.trim()}
          onClick={() => send({ reason, phrase })}>Clear it</button>
      </div>
      {/* R100/R38: the answer is DRAWN, or reading it buys nothing. A write that landed says nothing
          here on purpose — this route composes no sentence, and the phrase appearing in the list
          directly below IS the announcement (R66's ruling, met by the surface rather than by copy). */}
      {note ? <p className="owner-act-note" role="status">{note}</p> : null}
      <ul className="owner-lr__allowlist">
        {allowances.map((a) => (
          <li key={a.id}>
            <code>{a.phrase}</code>
            <span>{guidelines.find((g) => g.code === a.reason)?.rule || a.reason}</span>
            <button type="button" className="owner-link" onClick={() => send({ remove: a.id })}>Undo</button>
          </li>
        ))}
        {!allowances.length ? <li className="owner-page__note">None yet.</li> : null}
      </ul>
    </div>
  );
}
